import PageWrapper from "../../../pagecomponents/Common/PageWrapper";
import { Row, InputGroup, Button } from "react-bootstrap";
import { CCol, CFormInput, CFormLabel } from "@coreui/react";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getSupplierInvoice } from "../../../redux/slices/SupplierInvoiceSlice";
import { toastMessage, number_format } from "../../../helpers/utility";
import ModalWrapper from "../../../pagecomponents/Common/ModalWrapper";
import CreateSupplierInvoice from "./CreateSupplierInvoice";
import DataTable from "../../../pagecomponents/Common/DataTable";
import { ToastContainer } from "react-toastify";
import { createSupplierInvoice } from "../../../redux/slices/SupplierInvoiceSlice.js";
import Swal from "sweetalert2";

const SupplierInvoice = () => {
  const pageName = "Supplier Invoice";
  const dispatch = useDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState([]);
  const [searchedData, setSearchedData] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPage, setTotalPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Filters: only search now
  const [filters, setFilters] = useState({
    search: "",
  });

  const INIT_FORM_DATA = {
    filter: {
      search: "",
    },
    skip: 0,
    take: rowsPerPage,
  };

  const [formData, setFormData] = useState(INIT_FORM_DATA);

  useEffect(() => {
    getSupplierInvoiceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const getSupplierInvoiceData = async () => {
    setIsLoading(true);
    const { payload } = await dispatch(getSupplierInvoice({ inputData: formData }));
    if (payload?.status === "error") {
      setIsLoading(false);
      toastMessage("error", payload.msg || "An error occurred while fetching data.");
    } else {
      setTotalPage(Math.ceil(payload.count / rowsPerPage));
      setSearchedData(payload.data);
      setIsLoading(false);
    }
  };

  // Handle input changes for search
  const handleAutoComplete = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle search/filter data
  const handleGetData = () => {
    setCurrentPage(1);
    setFormData((prev) => ({
      ...prev,
      filter: {
        search: filters.search,
      },
      skip: 0,
    }));
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      search: "",
    });
    setCurrentPage(1);
    setFormData(INIT_FORM_DATA);
  };

  const openEditModal = (item) => {
    setModalVisible(true);
    setIsEdit(true);
    setEditData(item);
  };

  const openCreateInvoiceModal = () => {
    setModalVisible(true);
    setIsEdit(false);
  };

  const closePrintModal = () => {
    setModalVisible(false);
  };

  const handleSupplierInvoiceDelete = async (item) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      let params = {
        supplier_Invoice_Id: item.supplier_Invoice_Id,
        services: [],
        type: "delete",
      };

      const { payload } = await dispatch(createSupplierInvoice({ inputData: params }));
      if (payload?.status === "success") {
        Swal.fire("Deleted!", "Invoice has been deleted successfully.", "success");
        getSupplierInvoiceData();
      } else {
        toastMessage("error", payload.msg || "An error occurred while deleting.");
      }
    }
  };

  const handleRowsPerPageChange = (rows) => {
    setRowsPerPage(rows);
    setCurrentPage(1);

    setFormData((prev) => ({
      ...prev,
      take: rows,
      skip: 0,
    }));
  };

  const setPage = (pagenum) => {
    setCurrentPage(pagenum);
    setFormData((prev) => ({
      ...prev,
      skip: (pagenum - 1) * prev.take,
    }));
  };

  const columns = [
    {
      label: "Date",
      accessor: "invoice_Date",
      sortable: false,
      render: (value) => (value ? format(new Date(value), "dd/MM/yyyy") : "-"),
    },
    { label: "Supplier", accessor: "supplier_Name", sortable: false },
    { label: "Invoice No", accessor: "invoice_Number", sortable: false },
    {
      label: "Amount",
      accessor: "amount",
      sortable: false,
      render: (value) => (value ? number_format(value) : "0.00"),
    },
  ];

  const closeModal = () => {
    setModalVisible(false);
    setIsEdit(false);
  };

  return (
    <>
      <ToastContainer />
      <PageWrapper
        pageName={pageName}
        headerBtnTitle="+ Create Invoice"
        onHeaderButtonClick={openCreateInvoiceModal}
        showAddBtn={true}
      >
        {/* Search Section (only Supplier Name) */}
        <Row className="row-xs mb-4">
          <CCol md={4}>
            <CFormLabel
              className="main-content-label tx-11 tx-medium tx-gray-600"
              htmlFor="search"
            >
              Select Supplier Name
            </CFormLabel>
            <div className="hsc-wrapper">
              <InputGroup className="input-group">
                <CFormInput
                  type="text"
                  id="search"
                  name="search"
                  placeholder="Enter supplier name..."
                  value={filters.search}
                  onChange={handleAutoComplete}
                />
              </InputGroup>
            </div>
          </CCol>

          <CCol
            md={4}
            style={{
              display: "flex",
              alignItems: "flex-end",
              paddingTop: "28px",
              gap: "8px",
            }}
          >
            <Button
              variant=""
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleGetData}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Search"}
            </Button>
            <Button
              variant=""
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={handleResetFilters}
              disabled={isLoading}
            >
              Reset
            </Button>
          </CCol>
        </Row>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={searchedData}
          pageName={pageName}
          onDelete={handleSupplierInvoiceDelete}
          onEdit={openEditModal}
          currentPage={currentPage}
          totalPages={totalPage}
          rowsPerPage={rowsPerPage}
          updateRowsPerPage={handleRowsPerPageChange}
          onPageChange={setPage}
          loader={isLoading}
          editEnabled={true}
        />

        {/* Modal */}
        <ModalWrapper
          visible={modalVisible}
          onClose={closePrintModal}
          modalTitle={isEdit ? "Edit " + pageName : "Add " + pageName}
          modalSize="lg"
        >
          <CreateSupplierInvoice
            invisible={closeModal}
            loadData={getSupplierInvoiceData}
            mode={isEdit}
            editData={editData}
          />
        </ModalWrapper>
      </PageWrapper>
    </>
  );
};

export default SupplierInvoice;
